// eslint-disable-next-line no-restricted-imports
import '@/components/map-viewer/feature-popup.scss'
import XToolTip from '@/components/shared/x-tool-tip.vue'
import { setHTMLElementToMapLocation } from '@/lib/map-x/anyhtml'
import { createHTMLElement } from '@/lib/map-x/create-html-element'
import { isDrawing } from '@/lib/mapbox/draw-manager-plugin'
import { ref } from 'vue'
import MapElementWrapper from '@/components/map-x/map-element-wrapper.vue'
import { getMapEvents } from '@/composables/map-x/use-map-events'

/**
 * Adds feature popups for layer sources on mouseover
 * @param {MapMapViewer} mapViewer - Mapbox map instance
 * @param {array} sources - A list of layer source names to display popovers for
 */
export function initializeFeaturePopup({ mapViewer, sources }) {
  if (!sources?.length) {
    return
  }

  const map = mapViewer.map
  const eventEmitter = getMapEvents()
  if (!eventEmitter) {
    return
  }

  // ElementControls instance for managing popup lifecycle and positioning
  let featurePopup
  // Reactive reference for the current label text, will allow dynamic updates without requiring to recreate the popup
  const currentLabel = ref('')

  eventEmitter.on('map-x-mousemove', (event) => {
    // If we are using markup tools don't show popover
    if (isDrawing()) {
      return
    }

    const features = map.queryRenderedFeatures(event.detail.point)
    // Find features that match sources
    const feature = features.find((feature) => sources.includes(feature.source))

    // Display a popup with the name of the feature
    if (feature && feature.properties?.label) {
      currentLabel.value = feature.properties.label

      // if we haven't created the feature popup
      if (!featurePopup) {
        /**
         * Create HTMLElement from Vue components
         * We are relying on XToolTip to handle positioning relative to the map screen coords
         * wrapping in MapElementWrapper and passing a CSS class only ensures we can override the CSS specificity provided by XToolTip
         */
        const featurePopupElement = createHTMLElement(MapElementWrapper, {
          vueComponent: XToolTip,
          vueComponentProps: {
            trigger: 'always',
            delay: 0,
            direction: 'top',
            class: 'feature-popup',
            offset: 5,
            borderRadius: 'lg',
            arrowSize: 12,
          },
          vueComponentChildren: () => currentLabel.value,
          className: `pinned-element`,
        })

        /**
         * set the HTMLElement to map location and create a element controller
         */
        featurePopup = setHTMLElementToMapLocation(
          featurePopupElement,
          event.detail.coordinates,
          mapViewer,
          eventEmitter,
          'map-x-render',
          (next) => (featurePopup = next)
        )
      } else {
        /**
         * only need to set location on move
         * we have already created the HTMLElement at location
         * And the reactive currentLabel has adjusted the text value of the tooltip
         */
        featurePopup.setLocation(event.detail.coordinates)
      }
    } else {
      // clear popup
      featurePopup?.destroy()
    }
  })
  eventEmitter.on('map-x-mouseout', () => {
    // clear popup
    featurePopup?.destroy()
  })
}
